import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { PlanEntry } from '../api/planner';
import CookingTime from '../components/CookingTime';
import RecipeThumb from '../components/RecipeThumb';

/** One dinner in a week, however that week is being looked at.
 *
 * Shared by the current week and by the read-only past ones so the two cannot
 * drift into showing the same dinner differently. The controls arrive as a
 * slot rather than a `readOnly` flag on purpose: a past week passes nothing
 * and is then read-only by construction, instead of by somebody remembering
 * to hide two buttons every time this markup changes.
 *
 * Cost is likewise optional. It is priced from what the shops charge today,
 * which is a fact about this week and a fiction about any other.
 */
export default function PlanEntryCard({
  entry,
  cost,
  actions,
}: {
  entry: PlanEntry;
  cost?: string;
  actions?: ReactNode;
}) {
  // Where the recipe's back link should return to. Read here rather than passed
  // in, because this card is rendered by the page you are leaving — which is
  // what makes it right for both the current week and a past one, with nothing
  // for either caller to remember.
  const location = useLocation();

  return (
    <li className="card plan-entry">
      <Link
        to={`/recipes/${entry.recipe.id}`}
        state={{ from: location.pathname + location.search }}
        className="plan-entry__head"
      >
        <RecipeThumb id={entry.recipe.id} title={entry.recipe.title} size="md" />
        <span className="plan-entry__title">
          {entry.recipe.title}
          <span className="plan-entry__meta">
            <CookingTime recipe={entry.recipe} />
          </span>
        </span>
      </Link>
      {cost && <p className="plan-entry__cost">about ${cost}</p>}
      {entry.rationale && <p className="plan-entry__why">{entry.rationale}</p>}
      {entry.pairs_with.length > 0 && (
        <p className="plan-entry__pairs">
          shares {entry.pairs_with.slice(0, 3).join(', ')} with another dinner
        </p>
      )}
      {/* The cooked tick lives here rather than with the caller because it is
          the one piece of the row that a finished week still wants: it is what
          turns a list of what was suggested into a list of what was eaten. */}
      {(entry.status === 'cooked' || actions) && (
        <div className="plan-entry__actions">
          {entry.status === 'cooked' ? (
            /* The count only when it was not the planned one. Saying "for 2"
               on every ordinary dinner would make the number that matters --
               the week somebody cooked double -- stop standing out. */
            <span className="muted">
              Cooked ✓
              {/* Two reasons the count appears: the dinner was cooked for a
                  different number than it was planned for, or it was planned
                  for a different number than the week. Without the second,
                  a meal added for six and cooked for six prints nothing --
                  `cooked_servings` is null when the two agree.

                  Truthiness, not a null check: a plan cached before either
                  field existed has neither, and `undefined !== null` rendered
                  "for undefined" onto a finished week. The query cache is
                  offlineFirst, so that is a real payload. */}
              {entry.cooked_servings || entry.servings
                ? ` · for ${entry.cooked_servings || entry.servings}`
                : ''}
            </span>
          ) : (
            actions
          )}
        </div>
      )}
    </li>
  );
}
